import {
	useFetchBalanceSheets,
	useFetchFinancialPeriods,
	useFetchIncomeStatements,
} from "@portal/api/financials";
import { ReportedRetryableLoadError } from "@portal/components/features/load-errors";
import {
	FinancialMetrics,
	StatementMatrixGridWithAdjustments,
} from "@portal/components/financials";
import type {
	BalanceSheet,
	FinancialPeriodDto,
	IncomeStatement,
} from "@portal/components/financials/utils";
import { CenteredSpinner } from "@portal/components/ui/CenteredSpinner";
import { type FC, useCallback, useRef } from "react";
import type { AccountTabProps } from "../../account/AccountLayout";

const EMPTY_BALANCE_SHEETS: BalanceSheet[] = [];
const EMPTY_INCOME_STATEMENTS: IncomeStatement[] = [];
const EMPTY_FINANCIAL_PERIODS: FinancialPeriodDto[] = [];

type AccountFinancialsWithAdjustmentsTabProps = AccountTabProps;

export const AccountFinancialsWithAdjustmentsTab: FC<AccountFinancialsWithAdjustmentsTabProps> = ({
	accountId,
}) => {
	const {
		financialPeriods,
		financialPeriodsError,
		financialPeriodsIsFetching,
		financialPeriodsLoading,
		refetchFinancialPeriods,
	} = useFetchFinancialPeriods(accountId);
	const {
		balanceSheets,
		balanceSheetsError,
		balanceSheetsIsFetching,
		balanceSheetsLoading,
		refetchBalanceSheets,
	} = useFetchBalanceSheets(accountId);
	const {
		incomeStatements,
		incomeStatementsError,
		incomeStatementsIsFetching,
		incomeStatementsLoading,
		refetchIncomeStatements,
	} = useFetchIncomeStatements(accountId);

	const loading = financialPeriodsLoading || balanceSheetsLoading || incomeStatementsLoading;
	const retrying =
		financialPeriodsIsFetching || balanceSheetsIsFetching || incomeStatementsIsFetching;
	const error = financialPeriodsError ?? balanceSheetsError ?? incomeStatementsError;

	const lastErrorRef = useRef<Error | null>(null);
	if (error) {
		lastErrorRef.current = error;
	} else if (!retrying) {
		lastErrorRef.current = null;
	}
	const visibleError = error ?? (retrying ? lastErrorRef.current : null);

	const refetch = useCallback(() => {
		refetchFinancialPeriods();
		refetchBalanceSheets();
		refetchIncomeStatements();
	}, [refetchFinancialPeriods, refetchBalanceSheets, refetchIncomeStatements]);

	return (
		<div className="h-full flex flex-col">
			{visibleError ? (
				<ReportedRetryableLoadError
					error={visibleError}
					resource="financial data"
					onRetry={refetch}
					retrying={retrying}
					testId="financials-with-adjustments-error"
				/>
			) : loading ? (
				<CenteredSpinner />
			) : (
				<>
					<FinancialMetrics
						accountId={accountId}
						periods={financialPeriods ?? EMPTY_FINANCIAL_PERIODS}
						incomeStatements={incomeStatements ?? EMPTY_INCOME_STATEMENTS}
						balanceSheets={balanceSheets ?? EMPTY_BALANCE_SHEETS}
					/>
					<StatementMatrixGridWithAdjustments
						accountId={accountId}
						selectionResetKey={accountId}
						financialPeriods={financialPeriods ?? EMPTY_FINANCIAL_PERIODS}
						balanceSheets={balanceSheets ?? EMPTY_BALANCE_SHEETS}
						incomeStatements={incomeStatements ?? EMPTY_INCOME_STATEMENTS}
					/>
				</>
			)}
		</div>
	);
};
